import type { CfoGlDetailHandoff } from '../cfoGlDetailHandoff'
import { formatCurrencyExact, trimStr } from '../cfoFormat'
import type {
  CeoBasisKind,
  CeoCockpitResponse,
  CeoDecision,
  CeoDimension,
  CeoDimensionKind,
  CeoDimensionRow,
  CeoFreshness,
  CeoInventorySku,
  CeoMetricId,
  CeoMover,
  CeoPaceCurve,
  CeoPaceDay,
  CeoPeriodBlock,
  CeoPeriodKind,
  CeoScorecardMetric,
  CeoTrendPoint,
} from './ceoCockpitTypes'

export const SCORECARD_TILE_IDS: readonly CeoMetricId[] = [
  'sales',
  'gross_profit',
  'net_income',
  'cash',
  'accounts_receivable',
]

export const LENS_ORDER: readonly CeoDimensionKind[] = [
  'location',
  'sales_rep',
  'channel',
  'customer_type',
  'class',
  'item',
  'customer',
]

export const LENS_LABEL: Record<CeoDimensionKind, string> = {
  location: 'Location',
  sales_rep: 'Sales rep',
  channel: 'Channel',
  customer_type: 'Customer type',
  class: 'Class',
  item: 'Product',
  item_category: 'Category',
  employee: 'Employee',
  customer: 'Customer',
}

const BASIS_FALLBACK: readonly CeoBasisKind[] = ['plan', 'forecast', 'prior_year', 'prior_period']

export function selectScorecardView(
  payload: CeoCockpitResponse | null | undefined,
  period: CeoPeriodKind,
): Array<{ metric: CeoScorecardMetric; block: CeoPeriodBlock }> {
  const tiles = SCORECARD_TILE_IDS
  const scorecard = Array.isArray(payload?.scorecard) ? payload.scorecard : []
  return tiles
    .map((id) => scorecard.find((metric) => metric.id === id))
    .filter((metric): metric is CeoScorecardMetric => Boolean(metric))
    .map((metric) => ({ metric, block: metric[period] }))
}

export function selectPace(
  payload: CeoCockpitResponse | null | undefined,
  metricId: CeoMetricId,
  period: CeoPeriodKind,
): CeoPaceCurve | null {
  const curves = payload?.pace_curves || []
  return (
    curves.find((curve) => curve.metric_id === metricId && curve.period === period) ||
    curves.find((curve) => curve.metric_id === metricId) ||
    null
  )
}

export function selectDimension(
  payload: CeoCockpitResponse | null | undefined,
  kind: CeoDimensionKind | null,
): CeoDimension | null {
  if (!payload) return null
  const dimensions = Array.isArray(payload.dimensions) ? payload.dimensions : []
  if (kind) return dimensions.find((dimension) => dimension.kind === kind) || null
  return firstPresentLens(payload)
}

export function firstPresentLens(payload: CeoCockpitResponse | null | undefined): CeoDimension | null {
  const dimensions = Array.isArray(payload?.dimensions) ? payload.dimensions : []
  for (const kind of LENS_ORDER) {
    const dimension = dimensions.find((item) => item.kind === kind)
    if (dimension) return dimension
  }
  return dimensions[0] || null
}

export function rankRows(dimension: CeoDimension | null, period: CeoPeriodKind, limit = 8): CeoDimensionRow[] {
  if (!dimension || !Array.isArray(dimension.rows)) return []
  const ranked = [...dimension.rows].sort((a, b) => {
    const aVal = a[period]?.actual ?? 0
    const bVal = b[period]?.actual ?? 0
    if (bVal !== aVal) return bVal - aVal
    return a.rank - b.rank
  })
  return ranked.slice(0, limit)
}

export function movers(dimension: CeoDimension | null): { up: CeoMover[]; down: CeoMover[] } {
  return {
    up: (Array.isArray(dimension?.movers?.up) ? dimension.movers.up : []).slice(0, 3),
    down: (Array.isArray(dimension?.movers?.down) ? dimension.movers.down : []).slice(0, 3),
  }
}

/** Prefer plan → forecast → prior year → prior period, skipping unavailable bases. */
export function defaultBasis(
  availability: CeoCockpitResponse['basis_availability'] | null | undefined,
  preferred?: CeoBasisKind | null,
): CeoBasisKind {
  if (preferred && availability?.[preferred]?.available !== false) return preferred
  for (const basis of BASIS_FALLBACK) {
    if (availability?.[basis]?.available !== false) return basis
  }
  return 'prior_period'
}

export function basisDelta(
  block: CeoPeriodBlock | null | undefined,
  basis: CeoBasisKind,
): { deltaAbs: number | null; deltaPct: number | null } {
  if (!block) return { deltaAbs: null, deltaPct: null }
  if (basis === 'plan') {
    return {
      deltaAbs: block.plan?.variance_abs ?? null,
      deltaPct: block.plan?.variance_pct ?? null,
    }
  }
  if (basis === 'forecast') {
    return {
      deltaAbs: block.forecast?.variance_abs ?? null,
      deltaPct: block.forecast?.variance_pct ?? null,
    }
  }
  if (basis === 'prior_year') {
    return {
      deltaAbs: block.prior_year.delta_abs ?? null,
      deltaPct: block.prior_year.delta_pct ?? null,
    }
  }
  return {
    deltaAbs: block.prior_period.delta_abs ?? null,
    deltaPct: block.prior_period.delta_pct ?? null,
  }
}

export function describeFreshness(freshness: CeoFreshness, asOfDate: string): {
  line: string
  stale: boolean
  reasons: string[]
} {
  const through = formatFriendlyDate(freshness.sales_data_through || freshness.gl_data_through || asOfDate)
  const synced = formatClock(freshness.latest_sync_at)
  const closed = freshness.latest_closed_period
    ? `books closed through ${formatMonthYear(freshness.latest_closed_period)}`
    : null
  const parts = [`Through yesterday, ${through}`]
  if (synced) parts.push(`synced ${synced}`)
  if (closed) parts.push(closed)
  const reasons = [freshness.stale_reason].filter((value): value is string => Boolean(trimStr(value)))
  return {
    line: parts.join(' \u00B7 '),
    stale: Boolean(freshness.is_stale),
    reasons,
  }
}

export function narrativeFallback(payload: CeoCockpitResponse): string {
  const scorecard = Array.isArray(payload?.scorecard) ? payload.scorecard : []
  const sales = scorecard.find((metric) => metric.id === 'sales')
  const block = sales?.mtd
  const actual = block?.actual
  const planVar = block?.plan?.variance_pct
  const company = trimStr(payload?.company?.name) || 'This company'
  const top = sortDecisions(payload?.decisions)[0]
  if (top?.kind === 'decide') {
    return `${company}: ${top.headline.replace(/\.$/, '')}.`
  }
  if (actual != null && planVar != null && Number.isFinite(planVar)) {
    const direction = planVar >= 0 ? 'ahead of' : 'behind'
    return `${company} is ${direction} plan on sales this month, at ${formatCurrencyExact(actual)}.`
  }
  if (actual != null) {
    return `${company} has posted ${formatCurrencyExact(actual)} of sales month to date.`
  }
  return `${company}: here is what needs a decision this morning.`
}

export function sortDecisions(decisions: CeoDecision[] | null | undefined): CeoDecision[] {
  const rankSeverity = (severity: string): number => {
    if (severity === 'bad') return 0
    if (severity === 'check') return 1
    if (severity === 'watch') return 2
    return 3
  }
  const rankKind = (kind: string): number => {
    if (kind === 'decide') return 0
    if (kind === 'watch') return 1
    return 2
  }
  return [...(Array.isArray(decisions) ? decisions : [])].sort((a, b) => {
    const sev = rankSeverity(a.severity) - rankSeverity(b.severity)
    if (sev !== 0) return sev
    const kind = rankKind(a.kind) - rankKind(b.kind)
    if (kind !== 0) return kind
    if (b.score !== a.score) return b.score - a.score
    return a.rank - b.rank
  })
}

export function glHandoffForDrill(
  drill: CeoDecision['drill'] | null | undefined,
  fallbackLabel?: string,
): CfoGlDetailHandoff | null {
  if (!drill) return null
  const params = drill.params || {}
  const filtersRaw = Array.isArray(params.gl_filters) ? params.gl_filters : []
  const filters = filtersRaw
    .map((item) => {
      const column = trimStr((item as { column?: unknown })?.column)
      const values = Array.isArray((item as { values?: unknown })?.values)
        ? ((item as { values: unknown[] }).values).map((value) => String(value))
        : []
      if (!column || !values.length) return null
      return { column, values }
    })
    .filter((item): item is { column: string; values: string[] } => Boolean(item))
  const startDate = trimStr(params.gl_start_date)
  const endDate = trimStr(params.gl_end_date)
  if (!filters.length && !startDate && !endDate) {
    if (drill.tab !== 'gl_detail') return null
  }
  return {
    search: '',
    startDate,
    endDate,
    filters,
    label: fallbackLabel || trimStr(params.kind) || 'CEO cockpit',
  }
}

export function notTrackedCopy(connectTarget: string | null | undefined): {
  title: string
  sentence: string
  steps: string[]
} {
  switch (connectTarget) {
    case 'quickbooks_locations':
      return {
        title: 'Location is not tracked yet',
        sentence: 'Turn on Locations in QuickBooks so each sale carries a site, warehouse, or office.',
        steps: [
          'In QuickBooks, open Account and settings → Advanced → Categories.',
          'Turn on Track locations, then assign a location on invoices and receipts.',
          'Sync again — this panel fills from DepartmentRef on your sales lines.',
        ],
      }
    case 'quickbooks_classes':
      return {
        title: 'Class is not tracked yet',
        sentence: 'Turn on Classes in QuickBooks to slice sales by practice group, channel, or department.',
        steps: [
          'In QuickBooks, open Account and settings → Advanced → Categories.',
          'Turn on Track classes, then tag invoice lines.',
          'Sync again — this panel reads ClassRef on each sales line.',
        ],
      }
    case 'sales_rep_field':
      return {
        title: 'Sales rep is not tracked yet',
        sentence: 'Add a Sales Rep custom field to invoices, or map the field you already use in Cockpit settings.',
        steps: [
          'In QuickBooks, add a custom field named Sales Rep on invoices.',
          'Fill it with the employee who owns the sale.',
          'Or open Cockpit settings and map the field you already use.',
        ],
      }
    case 'customer_types':
      return {
        title: 'Customer type is not tracked yet',
        sentence: 'Assign Customer types in QuickBooks so mix and concentration can be read without a CRM export.',
        steps: [
          'In QuickBooks, create Customer types (Sales → Customer types).',
          'Assign a type on each customer record.',
          'Sync again — this panel joins CustomerTypeRef onto sales.',
        ],
      }
    case 'items':
      return {
        title: 'Products are not tracked yet',
        sentence: 'Put items on invoice lines so mix, margin, and inventory can be read from the books.',
        steps: [
          'In QuickBooks, create Products and services for what you sell.',
          'Use item lines on invoices instead of a single account-only line.',
          'Sync again — this panel reads ItemRef, qty, and amount.',
        ],
      }
    case 'inventory':
      return {
        title: 'Inventory is not tracked yet',
        sentence: 'Turn on quantity-on-hand for inventory items so stock, turns, and stockouts can be watched daily.',
        steps: [
          'In QuickBooks, mark items as Inventory and set a reorder point.',
          'Receive bills with item lines so on-hand rolls forward.',
          'Sync again — this panel reads inventory position and COGS qty.',
        ],
      }
    case 'payroll':
      return {
        title: 'Labor is not tracked yet',
        sentence: 'Add employees and payroll journal lines so headcount and labor percent can be read from the books.',
        steps: [
          'Keep the Employee list current in QuickBooks.',
          'Post payroll with employee entities on the journal lines.',
          'Sync again — this panel reads employees, payroll accounts, and TimeActivity.',
        ],
      }
    case 'billing':
      return {
        title: 'Recurring revenue is not tracked yet',
        sentence: 'Tag subscription items or map them in Cockpit settings so MRR, cohorts, and NRR can be computed.',
        steps: [
          'Name recurring items with subscription / monthly / annual / license.',
          'Or list those item ids in Cockpit settings.',
          'Sync again — this panel gates on recurring share of sales.',
        ],
      }
    case 'pos':
      return {
        title: 'Channel is not tracked yet',
        sentence: 'Map a QuickBooks field to Channel in Cockpit settings, or connect the POS that already knows dine-in vs delivery.',
        steps: [
          'Decide which QuickBooks field is your channel (Class, customer type, or custom field).',
          'Map it in Cockpit settings.',
          'Sync again — mix and movers fill from that field.',
        ],
      }
    case 'crm':
      return {
        title: 'Customers are not tracked yet',
        sentence: 'Keep customer records on invoices so concentration, new logos, and at-risk A/R can be named.',
        steps: [
          'Use a real customer on every invoice (not a generic walk-in, unless that is the channel).',
          'Keep A/R open on those customers.',
          'Sync again — this panel joins sales and A/R by customer.',
        ],
      }
    default:
      return {
        title: 'This slice is not tracked yet',
        sentence: 'The books do not yet carry this operating dimension. Feed it once and the panel stays on.',
        steps: [
          'Pick the QuickBooks field that already holds this slice.',
          'Map it in Cockpit settings, or start filling it on new transactions.',
          'Sync again — coverage is measured on the trailing twelve months.',
        ],
      }
  }
}

export function decisionKindLabel(kind: CeoDecision['kind']): string {
  if (kind === 'decide') return 'Decide'
  if (kind === 'watch') return 'Watch'
  return 'Good news'
}

export function decisionEdgeClass(decision: CeoDecision): string {
  if (decision.severity === 'bad') return 'bad'
  if (decision.severity === 'check' || decision.kind === 'decide') return 'check'
  if (decision.severity === 'good' || decision.kind === 'celebrate') return 'good'
  return 'watch'
}

/**
 * Sparkline-ready numbers from a `CeoTrendPoint[]` series. Null / non-finite
 * points are dropped. Tolerates a bare `number[]` at runtime (older snapshots,
 * hand-built fixtures) even though the contract type is points-only.
 */
export function trendValues(
  series: ReadonlyArray<CeoTrendPoint | number | null | undefined> | null | undefined,
): number[] {
  if (!Array.isArray(series) || !series.length) return []
  return series
    .map((item) => (typeof item === 'number' ? item : item?.value))
    .filter((value): value is number => value != null && Number.isFinite(value))
}

/**
 * Gross margin % for a SKU: `unit_margin / unit_price`, where the sell price is
 * `unit_cost + unit_margin` (the contract carries cost + margin, not price).
 * Falls back to the trailing-30-day `margin_30d / revenue_30d` when the unit
 * margin is not prepared. Returns null when neither denominator is usable —
 * NOT `unit_margin / unit_cost`, which is markup.
 */
export function skuGrossMarginPct(sku: Pick<CeoInventorySku, 'unit_cost' | 'unit_margin' | 'margin_30d' | 'revenue_30d'> | null | undefined): number | null {
  if (!sku) return null
  const margin = sku.unit_margin
  const cost = sku.unit_cost
  if (margin != null && Number.isFinite(margin) && cost != null && Number.isFinite(cost)) {
    const price = cost + margin
    if (price > 0) return margin / price
  }
  const rev = sku.revenue_30d
  const m30 = sku.margin_30d
  if (rev != null && Number.isFinite(rev) && rev > 0 && m30 != null && Number.isFinite(m30)) {
    return m30 / rev
  }
  return null
}

const DIMENSION_KINDS: readonly CeoDimensionKind[] = [
  'location',
  'class',
  'sales_rep',
  'employee',
  'customer_type',
  'channel',
  'item',
  'item_category',
  'customer',
]

export function isDimensionKind(value: unknown): value is CeoDimensionKind {
  return typeof value === 'string' && (DIMENSION_KINDS as readonly string[]).includes(value)
}

/**
 * The operating lens a decision card can be viewed through. Prefers an explicit
 * `lens` field if the server ever adds one; otherwise reads `drill.params.kind`
 * when the drill points at a dimension view (`view === 'dimension'`, on the
 * `ceo` tab or the GL detail tab). Null when the card has no lens.
 */
export function decisionLens(decision: Pick<CeoDecision, 'drill'> & { lens?: unknown } | null | undefined): CeoDimensionKind | null {
  if (!decision) return null
  if (isDimensionKind(decision.lens)) return decision.lens
  const drill = decision.drill
  if (!drill) return null
  const kind = (drill.params as Record<string, unknown> | null | undefined)?.kind
  if (drill.view === 'dimension' && (drill.tab === 'ceo' || drill.tab === 'gl_detail') && isDimensionKind(kind)) {
    return kind
  }
  return null
}

/** Which cumulative series on a pace day the selected basis compares against. */
export function paceBasisKey(basis: CeoBasisKind): 'plan_cum' | 'forecast_cum' | 'prior_year_cum' {
  if (basis === 'plan') return 'plan_cum'
  if (basis === 'forecast') return 'forecast_cum'
  // The pace curve carries no prior-period series; prior year is the closest ghost.
  return 'prior_year_cum'
}

/**
 * Days-left, needed-per-day, best day, and prior-year-same-day for the pace rail.
 * `daysElapsed`/`daysInPeriod` come from the calendar window that matches the
 * curve's period. Prior-year-same-day is read off the last actual day (not by
 * matching `day_index` to `days_elapsed`), so it survives a period mismatch and
 * any 0/1-based drift.
 */
export function paceRailStats(
  days: CeoPaceDay[] | null | undefined,
  basis: CeoBasisKind,
  daysElapsed: number,
  daysInPeriod: number,
): {
  daysLeft: number
  target: number | null
  needed: number | null
  best: { date: string; delta: number } | null
  priorYearSameDay: number | null
  lastActual: CeoPaceDay | null
} {
  const list = Array.isArray(days) ? days : []
  const key = paceBasisKey(basis)
  const daysLeft = Math.max(0, Math.round(daysInPeriod) - Math.round(daysElapsed))
  const lastActual = [...list].reverse().find((day) => day.actual_cum != null && Number.isFinite(day.actual_cum)) || null
  const lastBasis = [...list].reverse().find((day) => day[key] != null && Number.isFinite(day[key] as number)) || null
  const target = lastBasis ? (lastBasis[key] as number) : null
  const needed = target != null && lastActual?.actual_cum != null && daysLeft > 0
    ? Math.max(0, target - lastActual.actual_cum) / daysLeft
    : null
  let best: { date: string; delta: number } | null = null
  for (let index = 1; index < list.length; index += 1) {
    const day = list[index]
    const prev = list[index - 1]?.actual_cum
    if (day.actual_cum == null || prev == null || !Number.isFinite(day.actual_cum) || !Number.isFinite(prev)) continue
    const delta = day.actual_cum - prev
    if (!best || delta > best.delta) best = { date: day.date, delta }
  }
  const priorYearSameDay = lastActual?.prior_year_cum != null && Number.isFinite(lastActual.prior_year_cum)
    ? lastActual.prior_year_cum
    : null
  return { daysLeft, target, needed, best, priorYearSameDay, lastActual }
}

export function footerEvidenceLine(payload: CeoCockpitResponse): string {
  const through = formatFriendlyDate(payload?.as_of_date)
  const dimensions = Array.isArray(payload?.dimensions) ? payload.dimensions : []
  const missing = dimensions
    .filter((dimension) => dimension?.availability?.status === 'not_tracked')
    .map((dimension) => dimension.label)
  const missingBit = missing.length
    ? ` \u00B7 Not tracked yet: ${missing.join(', ')} \u2014 connect \u2192`
    : ''
  const contractId = trimStr(payload?.contract?.contract_id) || 'quickbooks.ceo.cockpit'
  return `Numbers from your books through ${through} \u00B7 governed contract ${contractId}${missingBit}`
}

function formatFriendlyDate(value: string | null | undefined): string {
  const trimmed = trimStr(value)
  if (!trimmed) return 'yesterday'
  const date = new Date(`${trimmed.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return trimmed
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatMonthYear(value: string): string {
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function formatClock(value: string | null | undefined): string | null {
  const trimmed = trimStr(value)
  if (!trimmed) return null
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
